/**
 * Zones that abolished seasonal clock changes, and the display rules we apply
 * until every runtime we render on knows about it.
 *
 * British Columbia, Alberta, Manitoba and the Northwest Territories stopped
 * changing their clocks and now sit on their former summer offset year round —
 * America/Vancouver is UTC-7, America/Edmonton, America/Inuvik and
 * America/Yellowknife UTC-6, and America/Winnipeg UTC-5 from the 2026-11-01
 * fall-back onwards, which IANA models by ending the DST rules at a transition
 * that becomes a no-op.
 *
 * We cannot wait for that to arrive on the viewer's machine. Luxon and dayjs
 * both resolve zone rules through `Intl`, which reads the JS engine's own
 * copy of the IANA database — shipped with Chrome, with iOS, with the Android
 * system image. Every one of those still carries the old fall-back, so a
 * correctly stored November instant renders an hour early and labelled PST.
 * That is a display bug, but not only one: the game views convert an admin's
 * typed wall clock back to UTC through the same rules, so an edit made on a
 * stale runtime writes the hour back into the database.
 *
 * Two independent corrections live here:
 *
 * 1. **Offset** — computations at or after the changeover are resolved through
 *    a zone that is permanently on the right offset in every tz release
 *    (America/Phoenix is UTC-7 forever, America/Regina UTC-6 forever,
 *    America/Panama UTC-5 forever). Skipped when the runtime already knows
 *    the real rule, so this defers to the engine as browsers catch up rather
 *    than pinning our own answer forever.
 * 2. **Label** — applied always, including on an up-to-date runtime. IANA
 *    names the permanent offset after the standard zone that shares it, so a
 *    current engine calls a BC game "MST" and a stale one calls it "PST".
 *    Neither is what anyone in Vancouver says: the clocks stayed on daylight
 *    time, so the label is PDT. Alberta named its own: Alberta Time, ABT.
 *    https://www.alberta.ca/albertas-new-time-system-abt
 */

interface PermanentDstRule {
  /** The abolished transition as an absolute instant. */
  changeoverUtcMs: number
  /**
   * The same transition as a wall clock, for deciding which rules a typed
   * local time falls under — the instant boundary would misfile every local
   * time between midnight and the UTC changeover that day.
   *
   * Set to the START of the changeover date, not to the abolished 02:00
   * transition. Nothing moves: the hours before 02:00 that day were already on
   * the summer offset under the old rules, and the hour the old rules repeated
   * resolves to the same instant either way. What it does remove is a phantom
   * ambiguity — a stale runtime reports 01:30 that morning as happening twice,
   * and in a province that no longer changes its clocks it happens once.
   */
  changeoverWallClockMs: number
  /** A zone permanently on the offset this zone now keeps, in every tz release. */
  fixedOffsetZone: string
  /** What the people who live there call it. */
  label: string
}

const PERMANENT_DST_RULES: Record<string, PermanentDstRule> = {
  'America/Vancouver': {
    changeoverUtcMs: Date.UTC(2026, 10, 1, 9),
    changeoverWallClockMs: Date.UTC(2026, 10, 1),
    fixedOffsetZone: 'America/Phoenix',
    label: 'PDT',
  },
  'America/Edmonton': {
    changeoverUtcMs: Date.UTC(2026, 10, 1, 8),
    changeoverWallClockMs: Date.UTC(2026, 10, 1),
    fixedOffsetZone: 'America/Regina',
    label: 'ABT',
  },
  'America/Winnipeg': {
    changeoverUtcMs: Date.UTC(2026, 10, 1, 7),
    changeoverWallClockMs: Date.UTC(2026, 10, 1),
    fixedOffsetZone: 'America/Panama',
    label: 'CDT',
  },
  'America/Inuvik': {
    changeoverUtcMs: Date.UTC(2026, 10, 1, 8),
    changeoverWallClockMs: Date.UTC(2026, 10, 1),
    fixedOffsetZone: 'America/Regina',
    label: 'MDT',
  },
  //  Now an alias of America/Edmonton in the tz database, but rules are looked
  //  up by the exact name a venue stores, so it needs its own row.
  'America/Yellowknife': {
    changeoverUtcMs: Date.UTC(2026, 10, 1, 8),
    changeoverWallClockMs: Date.UTC(2026, 10, 1),
    fixedOffsetZone: 'America/Regina',
    label: 'MDT',
  },
}

const OFFSET_FORMATTERS = new Map<string, Intl.DateTimeFormat | null>()

const offsetFormatter = (timeZone: string): Intl.DateTimeFormat | null => {
  if (OFFSET_FORMATTERS.has(timeZone)) {
    return OFFSET_FORMATTERS.get(timeZone) ?? null
  }

  let formatter: Intl.DateTimeFormat | null = null
  try {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    // Unknown zone, or a runtime built without full ICU. Callers treat null as
    // "cannot tell", which leaves behaviour exactly as it is today.
    formatter = null
  }

  OFFSET_FORMATTERS.set(timeZone, formatter)
  return formatter
}

/**
 * The zone's offset from UTC at an instant, in minutes, straight from `Intl`.
 *
 * Reads the wall clock the runtime reports for that instant and measures how
 * far it sits from the instant itself, which is the only way to ask a JS
 * engine what a zone's offset is without a date library.
 *
 * @param timeZone - IANA zone name
 * @param at - instant in epoch milliseconds
 * @returns offset in minutes (UTC-7 is -420), or null when it cannot be read
 */
export const zoneOffsetMinutes = (
  timeZone: string,
  at: number
): number | null => {
  const formatter = offsetFormatter(timeZone)
  if (formatter === null || !Number.isFinite(at)) {
    return null
  }

  const parts: Record<string, string> = {}
  formatter.formatToParts(new Date(at)).forEach((part) => {
    parts[part.type] = part.value
  })

  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  )

  return Number.isFinite(asUtc) ? Math.round((asUtc - at) / 60000) : null
}

const ABBREVIATION_FORMATTERS = new Map<string, Intl.DateTimeFormat | null>()

/**
 * The short name a runtime gives a zone at an instant, e.g. 'PST', 'MST',
 * 'GMT+9' — what a formatted string will contain when we have substituted a
 * zone and need to put the real label back.
 */
export const zoneAbbreviation = (timeZone: string, at: number): string => {
  if (!ABBREVIATION_FORMATTERS.has(timeZone)) {
    let formatter: Intl.DateTimeFormat | null = null
    try {
      formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'short',
      })
    } catch {
      formatter = null
    }
    ABBREVIATION_FORMATTERS.set(timeZone, formatter)
  }

  const formatter = ABBREVIATION_FORMATTERS.get(timeZone) ?? null
  if (formatter === null || !Number.isFinite(at)) {
    return ''
  }

  return (
    formatter
      .formatToParts(new Date(at))
      .find((part) => part.type === 'timeZoneName')?.value ?? ''
  )
}

const RUNTIME_KNOWS = new Map<string, boolean>()

/**
 * Whether this runtime's timezone database already carries the permanent
 * change for a zone — decided by asking it for an offset after the changeover
 * and seeing whether it agrees with the zone that is permanently on that
 * offset.
 *
 * Memoized: the answer cannot change while the page is open, and every date
 * rendered in a game list would otherwise re-derive it.
 */
export const runtimeKnowsPermanentDst = (timeZone: string): boolean => {
  const cached = RUNTIME_KNOWS.get(timeZone)
  if (cached !== undefined) {
    return cached
  }

  const rule = PERMANENT_DST_RULES[timeZone]
  if (rule === undefined) {
    return false
  }

  const probe = rule.changeoverUtcMs + 7 * 24 * 60 * 60 * 1000
  const actual = zoneOffsetMinutes(timeZone, probe)
  const expected = zoneOffsetMinutes(rule.fixedOffsetZone, probe)
  // A runtime that cannot answer is treated as stale, which is what it almost
  // certainly is — but the substitute zone it then gets is right either way.
  const knows = actual !== null && expected !== null && actual === expected

  RUNTIME_KNOWS.set(timeZone, knows)
  return knows
}

const substitute = (timeZone: string, rule: PermanentDstRule): string =>
  runtimeKnowsPermanentDst(timeZone) ? timeZone : rule.fixedOffsetZone

/**
 * The zone to compute an INSTANT in: the real zone, unless this runtime would
 * put it back on standard time.
 *
 * @param timeZone - the venue's / account's real IANA zone
 * @param atMs - the instant being rendered, in epoch milliseconds
 */
export const resolveDisplayZone = (timeZone: string, atMs: number): string => {
  const rule = PERMANENT_DST_RULES[timeZone]
  if (
    rule === undefined ||
    !Number.isFinite(atMs) ||
    atMs < rule.changeoverUtcMs
  ) {
    return timeZone
  }
  return substitute(timeZone, rule)
}

/**
 * The zone to convert a typed WALL CLOCK in — the write path.
 *
 * @param timeZone - the venue's / account's real IANA zone
 * @param wallClockAsUtcMs - the local date and time read as though it were UTC
 */
export const resolveWallClockZone = (
  timeZone: string,
  wallClockAsUtcMs: number
): string => {
  const rule = PERMANENT_DST_RULES[timeZone]
  if (
    rule === undefined ||
    !Number.isFinite(wallClockAsUtcMs) ||
    wallClockAsUtcMs < rule.changeoverWallClockMs
  ) {
    return timeZone
  }
  return substitute(timeZone, rule)
}

/**
 * The abbreviation to show for a zone that no longer changes its clocks, or
 * null when the normal one from `Intl` should stand.
 *
 * @param timeZone - the venue's / account's real IANA zone
 * @param atMs - the instant being labelled, in epoch milliseconds
 */
export const permanentDstLabel = (
  timeZone: string,
  atMs: number
): string | null => {
  const rule = PERMANENT_DST_RULES[timeZone]
  if (
    rule === undefined ||
    !Number.isFinite(atMs) ||
    atMs < rule.changeoverUtcMs
  ) {
    return null
  }
  return rule.label
}

/** Test seam: clears the memoized per-runtime detection. */
export const __resetPermanentDstDetection = (): void => {
  RUNTIME_KNOWS.clear()
  OFFSET_FORMATTERS.clear()
  ABBREVIATION_FORMATTERS.clear()
}
